import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate } from '../api/dataverseClient';
import { Budget, Expense } from '../types';

export async function getProjectBudgets(pca: IPublicClientApplication, projectId: string): Promise<Budget[]> {
  return dvList<Budget>(pca, 'dagrun_pm_budgets', {
    select: ['dagrun_pm_budgetid','dagrun_pm_name','dagrun_pm_budgettype','dagrun_pm_allocatedamount','dagrun_pm_currency','_dagrun_pm_projectid_value','_dagrun_pm_phaseid_value'],
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
  });
}

export async function createBudget(
  pca: IPublicClientApplication,
  projectId: string,
  name: string,
  budgetType: number,
  allocatedAmount: number,
  currency?: string | null,
  phaseId?: string | null,
): Promise<string> {
  const data: Record<string, unknown> = {
    dagrun_pm_name: name,
    dagrun_pm_budgettype: budgetType,
    dagrun_pm_allocatedamount: allocatedAmount,
    dagrun_pm_currency: currency ?? null,
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
  };
  if (phaseId) data['dagrun_pm_phaseid@odata.bind'] = `/dagrun_pm_phases(${phaseId})`;
  return dvCreate(pca, 'dagrun_pm_budgets', data);
}

export async function getProjectExpenses(pca: IPublicClientApplication, projectId: string): Promise<Expense[]> {
  return dvList<Expense>(pca, 'dagrun_pm_expenses', {
    select: ['dagrun_pm_expenseid','dagrun_pm_name','dagrun_pm_amount','dagrun_pm_currency','dagrun_pm_category','dagrun_pm_submittedby','dagrun_pm_submittedbyname','dagrun_pm_submittedon','dagrun_pm_approvalstatus','dagrun_pm_approvedby','dagrun_pm_receipturl','createdon','_dagrun_pm_projectid_value','_dagrun_pm_taskid_value'],
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
    orderby: 'createdon desc',
    top: 200,
  });
}

export async function createExpense(
  pca: IPublicClientApplication,
  projectId: string,
  description: string,
  amount: number,
  currency: string | null,
  category: number,
  taskId?: string | null,
): Promise<string> {
  const data: Record<string, unknown> = {
    dagrun_pm_name: description,
    dagrun_pm_amount: amount,
    dagrun_pm_currency: currency,
    dagrun_pm_category: category,
    dagrun_pm_approvalstatus: 100000000, // Pending
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
  };
  if (taskId) data['dagrun_pm_taskid@odata.bind'] = `/dagrun_pm_tasks(${taskId})`;
  return dvCreate(pca, 'dagrun_pm_expenses', data);
}

export async function submitExpense(
  pca: IPublicClientApplication,
  projectId: string,
  fields: {
    description: string; amount: number; currency: string;
    category: number; receipturl?: string | null;
    submittedby: string; submittedbyname: string;
  },
): Promise<string> {
  const data: Record<string, unknown> = {
    dagrun_pm_name: fields.description,
    dagrun_pm_amount: fields.amount,
    dagrun_pm_currency: fields.currency,
    dagrun_pm_category: fields.category,
    dagrun_pm_approvalstatus: 100000000, // Pending
    dagrun_pm_submittedby: fields.submittedby,
    dagrun_pm_submittedbyname: fields.submittedbyname,
    dagrun_pm_submittedon: new Date().toISOString(),
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
  };
  if (fields.receipturl) data.dagrun_pm_receipturl = fields.receipturl;
  return dvCreate(pca, 'dagrun_pm_expenses', data);
}

export async function decideExpense(
  pca: IPublicClientApplication,
  expenseId: string,
  decision: 'approve' | 'reject',
  approverOid: string,
): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_expenses', expenseId, {
    dagrun_pm_approvalstatus: decision === 'approve' ? 100000001 : 100000002,
    dagrun_pm_approvedby: approverOid,
  });
}
